import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUser,
  FaLock,
  FaMobileAlt,
  FaEnvelope,
  FaUserPlus,
  FaUnlock,
  FaArrowLeft,
} from "react-icons/fa";
import { FaPaste } from "react-icons/fa";

import styled from "styled-components";
import { motion } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TypingAnimation from "./TypingAnimation";
import backImage from "./assets/back.jpg";
import logo from "./assets/Logo2.png";
import okIcon from "./assets/ok.svg";

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-image: url(${backImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

// put near your other styled components
const BackLinksRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 12px;
`;

const OtpActionsRow = styled.div`
  width: 100%;
  max-width: 550px;
  display: flex;
  justify-content: flex-end;   /* right-aligned */
  margin: -6px 0 10px;         /* tuck closer to inputs */
`;

const PasteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 9999px;        /* pill */
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255, 255, 255, 0.18);  /* glass */
  backdrop-filter: blur(8px);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: transform .06s ease, background .2s ease, border-color .2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
    border-color: rgba(255,255,255,0.35);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px) scale(0.98);
  }

  svg { opacity: 0.9; }
`;


const InlineAlert = styled(motion.div)`
  width: 100%;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  font-weight: 700;
  color: #3f3f46;
  background: ${p =>
    p.$variant === "error"   ? "#fecaca" :   /* red-200 */
    p.$variant === "warning" ? "#fde68a" :   /* amber-200 */
                               "#bbf7d0"};   /* green-200 */
  border: 1px solid rgba(0,0,0,0.06);
`;


const BackLink = styled.button`
  background: transparent;
  border: none;
  color: rgba(46, 42, 42, 0.7);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  text-decoration: none;
  transition: color 0.2s ease, background 0.2s ease;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #4facfe;
    outline-offset: 2px;
  }
`;


const AdminLink = styled(Link)`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  padding: 10px;
  border-radius: 50%;
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  transition: all 0.3s ease-in-out;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
    color: #4facfe;
  }
`;

const SiteLink = styled.a`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(5px);
  padding: 8px 14px;
  border-radius: 9999px; /* pill */
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: black;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
    color: #4facfe;
  }
`;


const LoginForm = styled(motion.form)`
  font-family: "Inter", sans-serif;
  background: rgba(48, 136, 139, 0.15); /* Glass effect */
  backdrop-filter: blur(12px);
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0px 8px 32px rgba(7, 46, 90, 0.2);
  width: 100%;
  z-index:10;
  max-width: 550px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;
const Logo = styled.img`
  width: 200px;
  margin-bottom: 20px;
  filter: drop-shadow(0px 4px 8px rgba(255, 255, 255, 0.3));
`;

const InputField = styled.div`
  margin-bottom: 20px;
  position: relative;
  width: 100%;

  input {
    width: 100%;
    padding: 12px 50px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: bold; /*  Makes input text bold */
    color: #333;
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(5px);
    outline: none;
    transition: all 0.3s ease-in-out;

    &:focus {
      background: rgba(255, 255, 255, 0.3);
      box-shadow: 0px 0px 12px rgba(255, 255, 255, 0.4);
    }

    &::placeholder {
      font-weight: normal; /*  Placeholder remains normal-weight */
      color: rgba(46, 42, 42, 0.7);
    }
  }

  svg {
    position: absolute;
    top: 50%;
    left: 15px;
    transform: translateY(-50%);
    color: rgba(46, 42, 42, 0.7);
  }
`;

const Button = styled(motion.button)`
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  padding: 12px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  width: 100%;
  transition: all 0.3s ease-in-out;

  &:hover {
    background: linear-gradient(135deg, #00f2fe 0%, #4facfe 100%);
    transform: scale(1.05);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const IconContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 20px;
  img {
    width: 50px; /* Adjust size as needed */
    height: 50px;
  }
`;

const LinkContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px; /* Spacing between links */
  margin-top: 15px;
`;
const PhoneInputContainer = styled.div`
  display: flex;
  flex-direction: row; /*  Ensure input fields are in a row */
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap; /*  Prevent wrapping to new lines */
  gap: 5px; /*  Reduce spacing for better alignment */
  width: 100%;
  max-width: 420px; /*  Keep it wide enough */
  margin-bottom: 20px;
`;

const PhoneNumberInfo = styled.p`
  font-size: 14px;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.3);
  padding: 8px;
  border-radius: 6px;
  text-align: center;
  margin-bottom: 10px;
  font-weight: bold;
`;

const DigitInput = styled.input`
  width: 35px;
  height: 40px;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  border-color: #4facfe;
  color: black;
  outline: none;
  transition: all 0.2s ease-in-out;

  &:focus {
    background: rgba(255, 255, 255, 0.3);
    border-color: white;
    box-shadow: 0px 0px 10px rgba(255, 255, 255, 0.4);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.3);
    color: black;
    font-weight: bold;
    cursor: not-allowed;
  }
`;
const OtpInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px; /* Space between digits */
  width: 100%;
  max-width: 260px; /* Adjust width for 6 digits */
  margin-bottom: 20px;
`;

const OtpInfoMessage = styled.p`
  font-size: 14px;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.3);
  padding: 8px;
  border-radius: 6px;
  text-align: center;
  margin-bottom: 10px;
  font-weight: bold;
`;

const OtpMethodContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const OtpMethodButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 2px solid #4facfe;
  color: black;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
`;

const OtpDigitInput = styled.input`
  width: 40px;
  height: 50px;
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  color: black;
  outline: none;
  transition: all 0.2s ease-in-out;

  &:focus {
    background: rgba(255, 255, 255, 0.3);
    border-color: white;
    box-shadow: 0px 0px 10px rgba(255, 255, 255, 0.4);
  }
`;

const StyledLink = styled(Link)`
  color: rgba(46, 42, 42, 0.7);
  font-weight: bold;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px; /* Space between icon and text */
  transition: 0.3s;

  &:hover {
    color: white;
  }
`;

function Login() {
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState(""); // Store user phone number
  //const [showPhoneInput, setShowPhoneInput] = useState(false); // Show phone input after login
  const [isOtpSent, setIsOtpSent] = useState(false); // Track if OTP is sent
/*   const [phoneDigits, setPhoneDigits] = useState([
    "4",
    "8",
    " ",
    "",
    "",
    "",
    " ",
    "",
    "",
    "",
    " ",
    "",
    "",
    "",
  ]); */
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [showOtpSuccessMessage, setShowOtpSuccessMessage] = useState(false);
  const [showOtpMethodSelection, setShowOtpMethodSelection] = useState(false);
  const [sessionEmail, setSessionEmail] = useState(""); // Store user email from session
  const [showEmailOtpInput, setShowEmailOtpInput] = useState(false); // ✅ Track email OTP input
  const [isLicenseExpired, setIsLicenseExpired] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [inactiveAccount, setInactiveAccount] = useState(false);
  const [licenseErrorMessage, setLicenseErrorMessage] = useState("");


const REDIRECT_SECONDS = 4;


const sanitizeIdentifier = (value) =>
  value.replace(/\s+$/, "").toLowerCase(); // lowercase + no trailing spaces

const handleIdentifierChange = (e) => {
  setIdentifier(sanitizeIdentifier(e.target.value));
};

const handleIdentifierPaste = (e) => {
  const text = (e.clipboardData || window.clipboardData)?.getData("text") || "";
  e.preventDefault();
  setIdentifier(sanitizeIdentifier(text));
};

useEffect(() => {
  if (isLicenseExpired) {
    toast.info(`Zostaniesz przekierowany na stronę zakupu w ciągu ${REDIRECT_SECONDS} s.`);
    const timer = setTimeout(() => {
      window.location.href = "https://www.analizyiraporty.pl/cennik";
    }, REDIRECT_SECONDS * 1000);
    return () => clearTimeout(timer);
  }
}, [isLicenseExpired]);


const goBackToLogin = (e) => {
  e?.preventDefault();
  e?.stopPropagation();

  toast.dismiss();

  // clear inline alert + flags FIRST
  setLicenseErrorMessage("");     // <- clear message
  setIsLicenseExpired(false);     // <- clear license flag
  setInactiveAccount(false);      // <- clear inactive flag

  // reset OTP/UI
  setIsOtpSent(false);
  setShowEmailOtpInput(false);
 //setShowPhoneInput(false);
  setShowOtpMethodSelection(false);
  setShowOtpSuccessMessage(false);
  setOtpDigits(["", "", "", "", "", ""]);
};


 const handleLogin = async (e) => {
  e.preventDefault();
  setIsLoggingIn(true);


  setLicenseErrorMessage("");     // <- clear any previous inline message
  setIsLicenseExpired(false);     // <- clear license flag
  setInactiveAccount(false);

  // reset OTP/UI state before each attempt
  setIsOtpSent(false);
  setShowEmailOtpInput(false);
  //setShowPhoneInput(false);
  setShowOtpMethodSelection(false);
  setShowOtpSuccessMessage(false);
  setInactiveAccount(false);
  setIsLicenseExpired(false);
  setOtpDigits(["", "", "", "", "", ""]);

  try {
    // forbid trailing space in identifier
    if (/\s$/.test(identifier)) {
      toast.error("Brak spacji na końcu w polu login/email.");
      return;
    }
    const cleanIdentifier = identifier.trim();

    // 1) login
    const { data } = await axios.post(
      "https://server229256.nazwa.pl/login",
      { identifier: cleanIdentifier, password },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      }
    );

    const { user, token } = data || {};
    if (!user || !token) {
      throw new Error("Invalid login response: missing user or token");
    }

    // 2) persist session
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("token", token);

    // 3) decode JWT: schedule auto-logout + block if license expired
    try {
      const decoded = jwtDecode(token);

      // auto logout when JWT expires
      if (decoded?.exp) {
        const ms = decoded.exp * 1000 - Date.now();
        if (ms > 0) setTimeout(handleLogout, ms);
      }

      // SAFEGUARD: prevent OTP flow if license is expired in token
if (decoded?.LicenseExpire && new Date(decoded.LicenseExpire) < new Date()) {
  setIsLicenseExpired(true);
  setLicenseErrorMessage("Brak aktywnej licencji. Skontaktuj się z administratorem.");
  toast.error("Licencja wygasła. Skontaktuj się z administratorem.");
  setIsOtpSent(false);
  setShowEmailOtpInput(false);
  //setShowPhoneInput(false);
  setShowOtpMethodSelection(false);
  setShowOtpSuccessMessage(false);
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  return;
}
    } catch {
      // ignore decode problems; backend still guards license
    }

    // 4) keep email & phone for OTP
    setSessionEmail(user.Email || "");
    setPhoneNumber(user.Phone || ""); // backend sends "48XXXXXXXXX" when valid

    // 5) show OTP method chooser
    setShowOtpMethodSelection(true);
    toast.success("Logowanie zakończone. Wybierz metodę weryfikacji OTP.");
  } catch (error) {
    const status = error?.response?.status;
    const errMsg = error?.response?.data?.message || error?.response?.data?.error;

    if (status === 403) {
  if (typeof errMsg === "string" && /licencja|brak aktywnej licencji/i.test(errMsg)) {
    setIsLicenseExpired(true);
    setLicenseErrorMessage(errMsg || "Brak aktywnej licencji. Skontaktuj się z administratorem.");
    toast.error(errMsg || "Brak aktywnej licencji.");
  } else if (typeof errMsg === "string" &&
             /Konto nieaktywne|Twoje konto nie jest jeszcze aktywne/i.test(errMsg)) {
    setInactiveAccount(true);
    toast.error(
      errMsg ||
      "Twoje konto nie jest jeszcze aktywne. Sprawdź e-mail (także Spam/Oferty) lub napisz na biuro@analizyiraporty.pl."
    );
    setShowOtpMethodSelection(false);
   // setShowPhoneInput(false);
    setIsOtpSent(false);
  } else {
    toast.error(errMsg || "Odmowa dostępu.");
  }
}
 else if (status === 401) {
      toast.error("Nieprawidłowe dane logowania!");
    } else if (error.request) {
      toast.error("Brak odpowiedzi z serwera. Spróbuj ponownie.");
    } else {
      toast.error("Wystąpił nieoczekiwany błąd. Spróbuj ponownie.");
    }
  } finally {
    setIsLoggingIn(false);
  }
};


  // add near other handlers
const startSmsOtp = async () => {
  // use phoneNumber from profile only
  const num = (phoneNumber || "").replace(/\s/g, "");

  // must be PL format: 48 + 9 digits
  if (!/^48\d{9}$/.test(num)) {
    toast.error("Brak zapisanego numeru telefonu w profilu. Użyj weryfikacji e-mail.");
    setIsOtpSent(false);
    setShowEmailOtpInput(false);
    setShowOtpMethodSelection(true); // stay on chooser
    return;
  }

  try {
    await axios.post("https://server229256.nazwa.pl/send-otp", { phoneNumber: num });

    setIsOtpSent(true);          // show OTP inputs
    setShowEmailOtpInput(false); // SMS flow
    setShowOtpMethodSelection(false);
    toast.info("Wysłaliśmy SMS z kodem OTP. Wprowadź go poniżej.");
  } catch (err) {
    const msg = err?.response?.data?.error || err?.response?.data?.errors?.[0]?.message;
    if (msg?.toLowerCase().includes("limited")) {
      toast.info("Konto SMSAPI w trybie testowym – użyj OTP e-mail.");
    } else {
      toast.error(msg || "Nie udało się wysłać SMS z kodem. Wybierz e-mail.");
    }
    setIsOtpSent(false);
    setShowEmailOtpInput(false);
    setShowOtpMethodSelection(true); // back to chooser
  }
};


/*   const handlePhoneDigitChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return; // Allow only digits

    const newDigits = [...phoneDigits];

    // Prevent modifying the first two digits ("48")
    if (index < 2) return;

    newDigits[index] = value;
    setPhoneDigits(newDigits);

    // Move focus to next field unless it's a space position
    let nextIndex = index + 1;
    while ([2, 6, 10].includes(nextIndex)) nextIndex++; // Skip space positions
    if (nextIndex < phoneDigits.length) {
      document.getElementById(`digit-${nextIndex}`)?.focus();
    }
  }; */

/*   // Handle backspace behavior
  const handlePhoneDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && index > 2 && !phoneDigits[index]) {
      let prevIndex = index - 1;
      while ([3, 7, 11].includes(prevIndex)) prevIndex--; // Skip spaces
      document.getElementById(`digit-${prevIndex}`)?.focus();
    }
  }; */
/* 
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();

    //  Remove spaces and join digits into a full number
    const formattedPhoneNumber = phoneDigits.join("").replace(/\s/g, "");

    if (formattedPhoneNumber.length !== 11) {
      toast.error("Nieprawidłowy numer telefonu. Sprawdź wprowadzone cyfry.");
      return;
    }

    //  Store the formatted phone number for later use
    setPhoneNumber(formattedPhoneNumber);

    console.log("Sending OTP to:", formattedPhoneNumber); //  Debugging

    try {
      await axios.post("https://server229256.nazwa.pl/send-otp", {
        phoneNumber: formattedPhoneNumber, //  Send correct format
      });

      toast.success("Kod OTP został wysłany pomyślnie!");
      setIsOtpSent(true);
    } catch (error) {
      toast.error("Nie udało się wysłać kodu OTP.");
    }
  };
 */
  const handleOtpDigitChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return; // Allow only digits

    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);

    // Move focus to next input if a digit is entered
    if (value && index < otpDigits.length - 1) {
      document.getElementById(`otp-digit-${index + 1}`).focus();
    }
  };

  // Handle backspace to move focus back
  const handleOtpDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      document.getElementById(`otp-digit-${index - 1}`).focus();
    }
  };

const verifyOTP = async () => {
  const otpCode = otpDigits.join("");

  if (otpCode.length !== 6) {
    toast.error("Proszę wprowadzić poprawny 6-cyfrowy kod OTP.");
    return;
  }

  if (!/^48\d{9}$/.test((phoneNumber || "").replace(/\s/g, ""))) {
    toast.error("Brak zapisanego numeru telefonu w profilu. Użyj weryfikacji e-mail.");
    setIsOtpSent(false);
    setShowEmailOtpInput(false);
    setShowOtpMethodSelection(true);
    return;
  }

  try {
    const response = await axios.post("https://server229256.nazwa.pl/verify-otp", {
      phoneNumber: (phoneNumber || "").replace(/\s/g, ""),
      code: otpCode,
    });

    if (response.status === 204) {
      toast.success("Kod OTP zweryfikowany! Przekierowywanie...");
      setShowOtpSuccessMessage(true);
      setTimeout(() => navigate("/sprzedaz/dashboard"), 4000);
    }
  } catch (error) {
    if (error.response?.status === 400) {
      toast.error("❌ Niepoprawny kod OTP! Spróbuj ponownie.");
    } else if (error.response?.status === 408) {
      toast.error("⏳ Kod OTP wygasł. Poproś o nowy.");
    } else {
      toast.error("⚠ Weryfikacja kodu OTP nie powiodła się. Spróbuj ponownie.");
    }
  }
};


  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const sendEmailOtp = async () => {
    if (!sessionEmail) {
      toast.error("Nie znaleziono adresu e-mail w sesji.");
      return;
    }

    // ✅ Ensure UI updates immediately
    setIsOtpSent(true);
    setShowEmailOtpInput(true);

    try {
      await axios.post("https://server229256.nazwa.pl/send-email-otp", {
        email: sessionEmail, // ✅ Send email to backend
      });

      toast.success("Kod OTP został wysłany na Twój email!");
    } catch (error) {
      toast.error("Nie udało się wysłać kodu OTP na email.");
      setIsOtpSent(false); // ❌ Reset state if request fails
      setShowEmailOtpInput(false);
    }
  };

  const verifyEmailOtp = async () => {
    const otpCode = otpDigits.join(""); // ✅ Combine OTP digits

    if (otpCode.length !== 6) {
      toast.error("Proszę wprowadzić poprawny 6-cyfrowy kod OTP.");
      return;
    }

    try {
      const response = await axios.post(
        "https://server229256.nazwa.pl/verify-email-otp",
        {
          email: sessionEmail, // ✅ Send user email
          otp: otpCode, // ✅ Send OTP for verification
        }
      );

      if (response.status === 204) {
        toast.success("Kod OTP zweryfikowany! Przekierowywanie...");
        setShowOtpSuccessMessage(true);
        setTimeout(() => navigate("/sprzedaz/dashboard"), 4000);
      }
    } catch (error) {
      console.error(
        "❌ Verification error:",
        error.response?.data || error.message
      );

      if (error.response?.status === 400) {
        toast.error("❌ Niepoprawny kod OTP! Spróbuj ponownie.");
      } else if (error.response?.status === 404) {
        toast.error("⚠ Nie znaleziono użytkownika.");
      } else if (error.response?.status === 408) {
        toast.error("⏳ Kod OTP wygasł. Poproś o nowy.");
      } else {
        toast.error(
          "⚠ Weryfikacja kodu OTP nie powiodła się. Spróbuj ponownie."
        );
      }
    }
  };


  // Put near other constants/handlers
const OTP_LENGTH = 6;

const handleOtpPaste = (startIndex, e) => {
  const clip = (e.clipboardData || window.clipboardData)?.getData("text") || "";
  const digits = clip.replace(/\D/g, "").slice(0, OTP_LENGTH);
  if (!digits) return;

  e.preventDefault();

  setOtpDigits((prev) => {
    const next = [...prev];
    for (let i = 0; i < digits.length && startIndex + i < OTP_LENGTH; i++) {
      next[startIndex + i] = digits[i];
    }
    return next;
  });

  // focus next empty or last filled
  const nextIndex = Math.min(startIndex + digits.length, OTP_LENGTH - 1);
  requestAnimationFrame(() => {
    const firstEmpty = [...Array(OTP_LENGTH).keys()].find(i => !document.getElementById(`otp-digit-${i}`)?.value);
    const focusIndex = typeof firstEmpty === "number" ? firstEmpty : nextIndex;
    document.getElementById(`otp-digit-${focusIndex}`)?.focus();
  });
};

// (optional) auto-verify when all 6 digits present
useEffect(() => {
  if (isOtpSent && otpDigits.every(d => d && /^\d$/.test(d))) {
    if (showEmailOtpInput) {
      verifyEmailOtp();
    } else {
      verifyOTP();
    }
  }
}, [otpDigits, isOtpSent, showEmailOtpInput]); 


return (
  <LoginContainer>
    <ToastContainer
      position="top-center"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
    />

    <SiteLink
      href="https://www.analizyiraporty.pl/"
      onClick={(e) => {
        e.preventDefault();
        window.location.href = "https://www.analizyiraporty.pl/";
      }}
      aria-label="Powrót do strony głównej"
    >
      <FaArrowLeft size={14} />
      Powrót do strony
    </SiteLink>

    <AdminLink to="/adminpanel/adminlogin">
      <FaLock size={20} />
    </AdminLink>

    <LoginForm
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (isOtpSent) {
          showEmailOtpInput ? verifyEmailOtp() : verifyOTP();
        } else {
          handleLogin(e);
        }
      }}
    >
      <Logo src={logo} alt="Logo" />

      {isLicenseExpired && (
        <InlineAlert
          $variant="error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          ❌ {licenseErrorMessage || "Brak aktywnej licencji. Skontaktuj się z administratorem."}
          <br />
          Zostaniesz automatycznie przekierowany na stronę zakupu w ciągu{" "}
          <strong>{REDIRECT_SECONDS} sekund</strong>.
        </InlineAlert>
      )}

      {inactiveAccount && (
        <InlineAlert
          $variant="warning"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          ⚠️ Twoje konto nie jest jeszcze aktywne. Sprawdź wiadomość z linkiem aktywacyjnym. Jeśli nie dotarła, napisz na{" "}
          <a href="mailto:biuro@analizyiraporty.pl">biuro@analizyiraporty.pl</a>.
        </InlineAlert>
      )}

      {showOtpSuccessMessage ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
        >
          <TypingAnimation text="Sukces! Przekierowanie..." duration={100} className="text-green-500" />
          <IconContainer initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }}>
            <img src={okIcon} alt="Success" />
          </IconContainer>
        </motion.div>
      ) : showOtpMethodSelection ? (
        <>
          <OtpInfoMessage>Wybierz metodę uwierzytelnienia OTP</OtpInfoMessage>
          <OtpMethodContainer>
            <OtpMethodButton whileTap={{ scale: 0.95 }} onClick={startSmsOtp}>
              <FaMobileAlt /> Użyj SMS OTP
            </OtpMethodButton>

            <OtpMethodButton
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                sendEmailOtp();
                setShowOtpMethodSelection(false);
              }}
            >
              <FaEnvelope /> Użyj Email OTP
            </OtpMethodButton>
          </OtpMethodContainer>

          <BackLinksRow>
            <BackLink
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={goBackToLogin}
              aria-label="Powrót do logowania"
            >
              <FaArrowLeft /> Powrót do logowania
            </BackLink>
          </BackLinksRow>
        </>
      ) : isOtpSent ? (
        <>
          <OtpInfoMessage>
            Proszę wprowadzić 6-cyfrowy kod OTP wysłany na Twój {showEmailOtpInput ? "email" : "numer telefonu"}
          </OtpInfoMessage>

          <OtpInputContainer>
            {otpDigits.map((digit, index) => (
              <OtpDigitInput
                key={index}
                id={`otp-digit-${index}`}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                autoComplete="one-time-code"
                aria-label={`Cyfra OTP ${index + 1} z ${OTP_LENGTH}`}
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpDigitKeyDown(index, e)}
                onPaste={(e) => handleOtpPaste(index, e)}
              />
            ))}
          </OtpInputContainer>

          <OtpActionsRow>
            <PasteButton
              type="button"
              title="Wklej kod z schowka"
              onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText();
                  const fakeEvent = {
                    preventDefault: () => {},
                    clipboardData: { getData: () => text },
                  };
                  handleOtpPaste(0, fakeEvent);
                } catch {
                  toast.info("Nie udało się wczytać ze schowka. Przytrzymaj i wklej w pierwszym polu.");
                }
              }}
            >
              <FaPaste size={14} />
              Wklej kod
            </PasteButton>
          </OtpActionsRow>

          <Button
            type="button"
            whileTap={{ scale: 0.95 }}
            onClick={showEmailOtpInput ? verifyEmailOtp : verifyOTP}
          >
            Zweryfikuj kod
          </Button>

          <BackLinksRow>
            <BackLink
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={goBackToLogin}
              aria-label="Powrót do logowania"
            >
              <FaArrowLeft /> Powrót do logowania
            </BackLink>
          </BackLinksRow>
        </>
      ) : (
        <>
          <InputField>
            <FaUser />
<input
  type="text"
  placeholder="Email lub nazwa użytkownika"
  value={identifier}
  onChange={handleIdentifierChange}
  onPaste={handleIdentifierPaste}
  autoCapitalize="none"
  autoCorrect="off"
  spellCheck={false}
  required
/>

          </InputField>

          <InputField>
            <FaLock />
            <input
              type="password"
              placeholder="Hasło"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </InputField>

          <Button
            type="submit"
            whileTap={{ scale: 0.95 }}
            whileHover={{ backgroundColor: "#0056b3" }}
            disabled={isLoggingIn}
          >
            {isLoggingIn ? "Logowanie..." : "Zaloguj się"}
          </Button>

          <LinkContainer>
            <StyledLink to="/register">
              <FaUserPlus /> Zarejestruj się tutaj
            </StyledLink>

            <StyledLink to="/forgot-password">
              <FaUnlock /> Zapomniałeś hasła?
            </StyledLink>
          </LinkContainer>
        </>
      )}
    </LoginForm>
  </LoginContainer>
);


}

export default Login;
